import React from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { FinancialAccount } from "@common/types/financialAccount";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { canReconcileFinancialAccount } from "@helpers/financialAccount";
import { useContinuousReconciliationToggle } from "@hooks/useContinuousReconciliationToggle";
import { BankReconciliationRow } from "../bankReconciliationViewConfig";

type ContinuousReconciliationToggleValue = "yes" | "no";

interface ContinuousReconciliationToggleProps {
    financialAccount: FinancialAccount;
}

const ContinuousReconciliationToggle: React.FC<ContinuousReconciliationToggleProps> =
    React.memo(({ financialAccount }) => {
        const { isEnabled, disabledReason, setEnabled, isPending } =
            useContinuousReconciliationToggle(financialAccount);
        const toggleValue: ContinuousReconciliationToggleValue = isEnabled
            ? "yes"
            : "no";

        const handleValueChange = (
            next: ContinuousReconciliationToggleValue | null,
        ) => {
            if (next === null || next === toggleValue) {
                return;
            }
            setEnabled(next === "yes");
        };

        return (
            <ViewTableCell captureClicks>
                <Tooltip content={disabledReason}>
                    <ToggleGroup.Root<ContinuousReconciliationToggleValue>
                        value={toggleValue}
                        onValueChange={handleValueChange}
                        size="lg"
                        disabled={isPending || !!disabledReason}
                        aria-label="Continuous reconciliation"
                    >
                        <ToggleGroup.Item value="yes">Yes</ToggleGroup.Item>
                        <ToggleGroup.Item value="no">No</ToggleGroup.Item>
                    </ToggleGroup.Root>
                </Tooltip>
            </ViewTableCell>
        );
    });

ContinuousReconciliationToggle.displayName = "ContinuousReconciliationToggle";

export const ContinuousReconciliationCell: React.FC<
    CellContext<ViewTableFeatures, BankReconciliationRow, unknown>
> = React.memo(({ row }) => {
    const { financialAccount, bankReconciliation } = row.original;

    if (bankReconciliation || !canReconcileFinancialAccount(financialAccount)) {
        return <ViewTableCell />;
    }

    return (
        <ContinuousReconciliationToggle financialAccount={financialAccount} />
    );
});

ContinuousReconciliationCell.displayName = "ContinuousReconciliationCell";
